(() => {
  "use strict";

  window.zag = window.zag || {};

  window.zag.components.ZagProductGrid = class extends (
    window.zag.components.ZagComponent
  ) {
    connectedCallback() {
      if (this.seeMoreButton) {
        this.events.add(this.seeMoreButton, "click", this.onSeeMoreClick);
      }

      this.events.add(
        this.showSortOptionsButton,
        "click",
        this.onShowSortOptionsClick
      );
      this.events.add(document, "click", this.onDocumentClick);

      this.sortOptions.forEach((option) => {
        this.events.add(option, "click", this.onSortOptionClick);
      });

      this.events.add(this.showFiltersButton, "click", this.openFiltersPopin);
      this.events.add(this.closePopinButton, "click", this.closeFiltersPopin);
      this.events.add(this.filtersPopinContainer, "click", (event) => {
        if (event.target === this.filtersPopinContainer) {
          this.closeFiltersPopin();
        }
      });

      this.events.add(
        this.confirmFilterSelectionButton,
        "click",
        this.onConfirmFilterSelectionClick
      );

      this.appliedFiltersCards.forEach((card) => {
        this.events.add(card, "click", this.onAppliedFilterCardClick);
      });

      this.events.add(
        this.clearFiltersButton,
        "click",
        this.onClearFiltersClick
      );

      this.filterValueCards.forEach((card) => {
        this.events.add(card, "click", this.onFilterValueCardClick);
      });

      this.events.add(
        this,
        "dual-handle-range:update",
        this.onPriceFilterUpdate
      );

      this.updateAppliedFilterCount();
    }

    get productGrid() {
      return this.cache.get("dom", "productGrid", () => {
        return this.querySelector(".product-grid");
      });
    }

    get seeMoreButton() {
      return this.cache.get("dom", "seeMoreButton", () => {
        return this.querySelector(".see-more-button");
      });
    }

    get showFiltersButton() {
      return this.cache.get("dom", "showFiltersButton", () => {
        return this.querySelector(".show-filters");
      });
    }

    get showSortOptionsButton() {
      return this.cache.get("dom", "showSortOptionsButton", () => {
        return this.querySelector(".show-sort-options");
      });
    }

    get sortOptionsContainer() {
      return this.cache.get("dom", "sortOptionsContainer", () => {
        return this.querySelector(".sort-options-container");
      });
    }

    get sortOptions() {
      return this.cache.get("dom", "sortOptions", () => {
        return Array.from(this.querySelectorAll(".sort-option"));
      });
    }

    get filtersPopinContainer() {
      return this.cache.get("dom", "filtersPopinContainer", () => {
        return this.querySelector(".filters-popin-container");
      });
    }

    get filtersPopin() {
      return this.cache.get("dom", "filtersPopin", () => {
        return this.querySelector(".filters-popin");
      });
    }

    get closePopinButton() {
      return this.cache.get("dom", "closePopinButton", () =>
        this.querySelector(".popin-close")
      );
    }

    get appliedFiltersCards() {
      return this.cache.get("dom", "appliedFiltersCards", () => {
        return Array.from(this.querySelectorAll(".applied-filter-card"));
      });
    }

    get appliedFiltersTitle() {
      return this.cache.get("dom", "appliedFiltersTitle", () => {
        return this.querySelector(".applied-filters-title");
      });
    }

    get appliedFilters() {
      return this.cache.get("dom", "appliedFilters", () => {
        return this.querySelector(".applied-filters");
      });
    }

    get appliedFilterCount() {
      return this.appliedFiltersCards.reduce((count, card) => {
        return count + (card.getAttribute("aria-selected") === "true" ? 1 : 0);
      }, 0);
    }

    get clearFiltersButton() {
      return this.cache.get("dom", "clearFiltersButton", () => {
        return this.querySelector(".clear-filters");
      });
    }

    get filterValueCards() {
      return this.cache.get("dom", "filterValueCards", () => {
        return Array.from(this.querySelectorAll(".filter-value"));
      });
    }

    get priceFilterSlider() {
      return this.cache.get("dom", "priceFilterSlider", () => {
        return this.querySelector("dual-handle-range");
      });
    }

    get confirmFilterSelectionButton() {
      return this.cache.get("dom", "confirmSelectionButton", () => {
        return this.querySelector(".confirm-filter-selection");
      });
    }

    get pageNumber() {
      return this.productGrid.dataset.pageNumber;
    }

    get productShownCount() {
      return this.productGrid.dataset.productShownCount;
    }

    get productShownText() {
      return this.cache.get("dom", "shownProductCount", () => {
        return this.querySelector(".product-count span");
      });
    }

    get endpoint() {
      const url = new URL(window.location);
      url.searchParams.set("page", Number(this.pageNumber) + 1);
      return url.toString();
    }

    onSeeMoreClick = async (event) => {
      if (this.modifiers.has("loading")) return;

      try {
        this.modifiers.add("loading");

        const response = await fetch(this.endpoint);
        if (!response.ok) throw new Error("Network response was not ok");

        const nextPageMarkup = await response.text();
        const nextPageHTML = new DOMParser().parseFromString(
          nextPageMarkup,
          "text/html"
        );
        const nextProductList = nextPageHTML.querySelector(".product-grid");

        if (!nextProductList)
          throw new Error("Could not find product list in the fetched page");

        const productHTMLList = Array.from(nextProductList.children)
          .map((child) => child.outerHTML)
          .join("");
        this.productGrid.insertAdjacentHTML("beforeend", productHTMLList);

        this.updateProgress(nextPageHTML);
      } catch (error) {
        console.error("Error loading more products", error);
      } finally {
        this.modifiers.remove("loading");
      }
    };

    onShowSortOptionsClick = (event) => {
      event.stopPropagation();

      if (this.showSortOptionsButton.hasAttribute("data-active")) {
        this.showSortOptionsButton.removeAttribute("data-active");
      } else {
        this.showSortOptionsButton.setAttribute("data-active", "");
      }
    };

    onSortOptionClick = (event) => {
      const sortOption = event.currentTarget;

      const url = new URL(window.location);
      url.searchParams.set("sort_by", sortOption.dataset.sortOption);
      window.location.href = url;
    };

    onDocumentClick = (event) => {
      if (!this.showSortOptionsButton.hasAttribute("data-active")) return;

      if (!this.sortOptionsContainer.contains(event.target)) {
        this.showSortOptionsButton.removeAttribute("data-active");
      }
    };

    openFiltersPopin = (event) => {
      if (event) event.preventDefault();
      window.zag.utils.documentScroll.isLocked = true;
      this.modifiers.add("filters-popin");
    };

    closeFiltersPopin = (event) => {
      if (event) event.preventDefault();
      window.zag.utils.documentScroll.isLocked = false;
      this.modifiers.remove("filters-popin");
    };

    updateProgress(nextPageHTML) {
      this.productGrid.dataset.pageNumber = Number(this.pageNumber) + 1;

      if (
        nextPageHTML.querySelector(".product-grid").dataset.pageNumber ===
        "last"
      ) {
        this.productGrid.dataset.pageNumber = "last";
        this.seeMoreButton.remove();
      }

      const currentProgress = this.querySelector(".product-count");
      const newProgress = nextPageHTML.querySelector(".product-count");
      currentProgress.querySelector("p").innerHTML =
        newProgress.querySelector("p").innerHTML;

      const currentProgressBar = currentProgress.querySelector(
        ".progress-bar-value"
      );
      const newProgressBar = newProgress.querySelector(".progress-bar-value");
      const currentPercentage = currentProgressBar.style.getPropertyValue(
        "--progress-percentage"
      );
      const newPercentage = newProgressBar.style.getPropertyValue(
        "--progress-percentage"
      );
      newProgressBar.style.setProperty(
        "--progress-percentage",
        currentPercentage
      );
      currentProgressBar.innerHTML = newProgressBar.innerHTML;

      currentProgressBar.style.setProperty(
        "--progress-percentage",
        newPercentage
      );

      const newProductShownCount =
        nextPageHTML.querySelector(".item-count").innerHTML;
      this.querySelector(".item-count").innerHTML = newProductShownCount;
    }

    onConfirmFilterSelectionClick = (event) => {
      event.preventDefault();

      const url = new URL(window.location);

      const paramsToDelete = [];
      for (const [key] of url.searchParams) {
        if (key.startsWith("filter") || key === "page") {
          paramsToDelete.push(key);
        }
      }
      paramsToDelete.forEach((param) => url.searchParams.delete(param));

      this.appliedFiltersCards.forEach((card) => {
        if (card.getAttribute("aria-selected") === "true") {
          if (card.dataset.url) {
            const filterParam = decodeURIComponent(card.dataset.url);
            const [key, value] = filterParam.split("=");
            url.searchParams.append(key, value);
          } else if (card.dataset.minUrl && card.dataset.maxUrl) {
            const minValue = Number(
              this.priceFilterSlider.minValue.dataset.value
            );
            const maxValue = Number(
              this.priceFilterSlider.maxValue.dataset.value
            );
            const minLimit = Number(this.priceFilterSlider.dataset.min);
            const maxLimit = Number(this.priceFilterSlider.dataset.max);

            if (minValue > minLimit) {
              const minUrl = card.dataset.minUrl.replace(
                "[[min_price]]",
                minValue.toString()
              );
              const [minKey, minVal] = decodeURIComponent(minUrl).split("=");
              url.searchParams.append(minKey, minVal);
            }

            if (maxValue < maxLimit) {
              const maxUrl = card.dataset.maxUrl.replace(
                "[[max_price]]",
                maxValue.toString()
              );
              const [maxKey, maxVal] = decodeURIComponent(maxUrl).split("=");
              url.searchParams.append(maxKey, maxVal);
            }
          }
        }
      });

      this.closeFiltersPopin();

      window.location.href = url.toString();
    };

    onAppliedFilterCardClick = (event) => {
      const card = event.currentTarget;
      if (!event.target.closest(".remove-filter-value")) return;

      event.preventDefault();
      event.stopPropagation();

      if (card.dataset.url) {
        const filterUrl = card.dataset.url;
        const correspondingFilterValue = this.filterValueCards.find(
          (filterCard) => filterCard.dataset.url === filterUrl
        );

        if (correspondingFilterValue) {
          correspondingFilterValue.setAttribute("aria-selected", "false");
        }
        card.setAttribute("aria-selected", "false");
      } else if (card.dataset.minUrl) {
        if (this.priceFilterSlider) {
          const minLimit = Number(this.priceFilterSlider.dataset.min);
          const maxLimit = Number(this.priceFilterSlider.dataset.max);
          this.priceFilterSlider.setValues(minLimit, maxLimit);

          const currencySymbol = card.dataset.currencySymbol || "";
          const paragraph = card.querySelector("p");
          if (paragraph) {
            paragraph.textContent = `${minLimit}-${maxLimit} ${currencySymbol}`;
          }
        }
        card.setAttribute("aria-selected", "false");
      }

      this.updateAppliedFilterCount();
    };

    onClearFiltersClick = (event) => {
      event.preventDefault();

      this.filterValueCards.forEach((card) => {
        card.setAttribute("aria-selected", "false");
      });

      this.appliedFiltersCards.forEach((card) => {
        card.setAttribute("aria-selected", "false");
      });

      if (this.priceFilterSlider) {
        const minLimit = Number(this.priceFilterSlider.dataset.min);
        const maxLimit = Number(this.priceFilterSlider.dataset.max);
        this.priceFilterSlider.setValues(minLimit, maxLimit);

        const priceAppliedCard = this.appliedFiltersCards.find(
          (card) => card.dataset.minUrl
        );
        if (priceAppliedCard) {
          const currencySymbol = priceAppliedCard.dataset.currencySymbol || "";
          const paragraph = priceAppliedCard.querySelector("p");
          if (paragraph) {
            paragraph.textContent = `${minLimit}-${maxLimit} ${currencySymbol}`;
          }
        }
      }

      this.updateAppliedFilterCount();
    };

    onFilterValueCardClick = (event) => {
      const card = event.currentTarget;
      event.preventDefault();

      const isCurrentlySelected = card.getAttribute("aria-selected") === "true";
      const newState = !isCurrentlySelected;

      card.setAttribute("aria-selected", newState.toString());

      const filterUrl = card.dataset.url;
      const correspondingAppliedCard = this.appliedFiltersCards.find(
        (appliedCard) => appliedCard.dataset.url === filterUrl
      );

      if (correspondingAppliedCard) {
        correspondingAppliedCard.setAttribute(
          "aria-selected",
          newState.toString()
        );
      }

      this.updateAppliedFilterCount();
    };

    onPriceFilterUpdate = (event) => {
      const { minValue, maxValue, minLimit, maxLimit } = event.detail;

      const priceAppliedCard = this.appliedFiltersCards.find(
        (card) => card.dataset.minUrl
      );

      if (priceAppliedCard) {
        const isMinSet = minValue > minLimit;
        const isMaxSet = maxValue < maxLimit;
        const isPriceFilterActive = isMinSet || isMaxSet;

        priceAppliedCard.setAttribute(
          "aria-selected",
          isPriceFilterActive.toString()
        );

        const currencySymbol = priceAppliedCard.dataset.currencySymbol || "";
        const paragraph = priceAppliedCard.querySelector("p");
        if (paragraph) {
          paragraph.textContent = `${minValue}-${maxValue} ${currencySymbol}`;
        }

        this.updateAppliedFilterCount();
      }
    };

    updateAppliedFilterCount = () => {
      const count = this.appliedFilterCount;

      // Update the applied filters title span with new count
      if (this.appliedFiltersTitle) {
        const span = this.appliedFiltersTitle.querySelector("span");
        if (span) {
          span.textContent = count;
        }
      }

      // Update the data-applied-filter-count attribute on the applied filters container
      if (this.appliedFilters) {
        this.appliedFilters.setAttribute("data-applied-filter-count", count);
      }
    };

    hideFilterCard = (card) => {
      card.setAttribute("aria-selected", "false");
    };

    showFilterCard = (card) => {
      card.setAttribute("aria-selected", "true");
    };
  };
  if (!window.customElements.get("zag-product-grid")) {
    window.customElements.define(
      "zag-product-grid",
      window.zag.components.ZagProductGrid
    );
  }

  window.zag.components.DualHandleRange = class extends (
    window.zag.components.ZagComponent
  ) {
    constructor() {
      super();

      this.activeHandle = null;
      this.dragState = {
        isDragging: false,
        top: 0,
        left: 0,
        x: 0,
        y: 0,
        startTime: 0,
      };
    }

    connectedCallback() {
      this.updateUI();

      this.events.add(this.minHandle, "pointerdown", (event) =>
        this.onPointerDown(event, this.minHandle)
      );
      this.events.add(this.maxHandle, "pointerdown", (event) =>
        this.onPointerDown(event, this.maxHandle)
      );
    }

    get minValue() {
      return this.cache.get("state", "minValue", () => {
        return this.querySelector(".range-value--min");
      });
    }

    get maxValue() {
      return this.cache.get("state", "maxValue", () => {
        return this.querySelector(".range-value--max");
      });
    }

    get minHandle() {
      return this.cache.get("dom", "minHandle", () => {
        return this.querySelector(".range-handle--min");
      });
    }

    get maxHandle() {
      return this.cache.get("dom", "maxHandle", () => {
        return this.querySelector(".range-handle--max");
      });
    }

    get rangeFill() {
      return this.cache.get("dom", "rangeFill", () => {
        return this.querySelector(".range-fill");
      });
    }

    onPointerDown = (event, handle) => {
      event.preventDefault();

      this.activeHandle = handle;
      this.dragState = {
        isDragging: true,
        x: event.clientX,
        y: event.clientY,
      };

      this.events.add(document, "pointermove", this.onPointerMove, {
        passive: false,
      });
      this.events.add(document, "pointerup", this.onPointerUp);
    };

    onPointerMove = (event) => {
      if (!this.dragState.isDragging) return;
      event.preventDefault();

      const rect = this.getBoundingClientRect();
      const relativeX = event.clientX - rect.left;
      const percentage = Math.max(
        0,
        Math.min(100, (relativeX / rect.width) * 100)
      );

      const minRangeValue = Number(this.dataset.min);
      const maxRangeValue = Number(this.dataset.max);

      const minHandleValue = Number(this.minValue.dataset.value);
      const maxHandleValue = Number(this.maxValue.dataset.value);

      const value = Math.round(
        (percentage / 100) * (maxRangeValue - minRangeValue) + minRangeValue
      );

      if (this.activeHandle === this.minHandle) {
        this.minValue.dataset.value = Math.min(value, maxHandleValue);
      } else {
        this.maxValue.dataset.value = Math.max(value, minHandleValue);
      }

      this.updateUI();
    };

    onPointerUp = (event) => {
      if (!this.dragState.isDragging) return;
      this.dragState.isDragging = false;

      this.events.remove(document, "pointermove", this.onPointerMove);
      this.events.remove(document, "pointerup", this.onPointerUp);

      this.dispatchEvent(
        new CustomEvent("dual-handle-range:update", {
          bubbles: true,
          detail: {
            minValue: Number(this.minValue.dataset.value),
            maxValue: Number(this.maxValue.dataset.value),
            minLimit: Number(this.dataset.min),
            maxLimit: Number(this.dataset.max),
          },
        })
      );
    };

    updateUI = () => {
      const minPercentage =
        ((this.minValue.dataset.value - this.dataset.min) /
          (this.dataset.max - this.dataset.min)) *
        100;
      const maxPercentage =
        ((this.maxValue.dataset.value - this.dataset.min) /
          (this.dataset.max - this.dataset.min)) *
        100;

      this.style.setProperty("--min-handle-offset", `${minPercentage}%`);
      this.style.setProperty("--max-handle-offset", `${maxPercentage}%`);
      this.style.setProperty("--range-fill-offset", `${minPercentage}%`);
      this.style.setProperty(
        "--range-fill-size",
        `${maxPercentage - minPercentage}%`
      );
    };

    setValues = (minValue, maxValue) => {
      const minLimit = Number(this.dataset.min);
      const maxLimit = Number(this.dataset.max);

      this.minValue.dataset.value = Math.max(
        minLimit,
        Math.min(minValue, maxValue)
      );
      this.maxValue.dataset.value = Math.min(
        maxLimit,
        Math.max(minValue, maxValue)
      );

      this.updateUI();
    };
  };
  if (!window.customElements.get("dual-handle-range")) {
    window.customElements.define(
      "dual-handle-range",
      window.zag.components.DualHandleRange
    );
  }
})();
